import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {connect} from "react-redux";
import PropTypes from "prop-types";

import SelectionMainList from "../../selectionWindow/selectionMainList";
import {getListItemHeight} from "../materialsCommon/materialsCommonFunctions";
import {setWizardSelectedValues} from "../../../actions/wizardFunctions";
import {browserIsSafari} from "../../../misc/helpers";
import {updateNavigationCount} from "../../../actions/lookFunctions";
import {wizardGroupInterface} from "../../../interfaces/wizardItemInterface";
import {useIsMounted} from "../../../misc/helperHooks";
import {
  getWizardMissingKey,
  shouldUseNewWizardFeaturesRequestsLogic
} from "../../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions";
import {shouldSwitchVisualizer, switchToNewVisualizerType} from "../../../misc/visualizerSwitchingFunctions";
import {UserPopupTypes} from "../../visComponents/constants";
import Analytics from "../../../analytics";
import {toggleUserPopup} from "../../../actions/actionFunctions";
import {fixFeature} from "../../sqft/components/popup/modalFix/CompatibilityWarnings/modalFixHelper";
import {
  getWizardFeatureForCurrentRoomLayout,
  shouldResolveWizardFeatureBeforeClick,
  switchRoomByWizardGroup,
  runWhileRoomSwitching
} from "../../visComponents/navigation/roomSwitchingFunctions";


const MaterialsWizardMainList = (props) => {

  const {setRef, currentParent, features, layoutName, selectionWindowView, isMobile, setWizardSelectedValues, bvOpened,
    wizardActiveStep, wizardGroup, updateNavigationCount, currentGroups, wizardMissingFeatures, fabricatorSettings,
    visualizerIsChanging, isPhoneNumberValidated, userIsLoggedIn, toggleUserPopup, wizardActiveItem,
    wizardSelectedValues} = props;

  const [resizing, setResizing] = useState(false);
  // Bumped whenever the container has (re)appeared in the DOM and `itemHeight` has to be
  // measured again. `itemsList.current` alone can't drive that: refs are attached AFTER the
  // render that created them, so the very first render always measures 0 and nothing
  // re-renders the component on its own to pick the real size up.
  const [measureId, setMeasureId] = useState(0);

  const itemsList = useRef();
  const resizeId = useRef();
  const prevItemHeight = useRef();
  const isMounted = useIsMounted();
  // Tracks the last known viewport size so we can ignore "resize" events that
  // don't actually change it (see onResize below).
  const viewportSize = useRef({width: window.innerWidth, height: window.innerHeight});

  const itemHeight = useMemo(() => {
    if (resizing && prevItemHeight.current) {
      return prevItemHeight.current
    }
    const height = isMobile && itemsList.current
      ? itemsList.current.clientHeight
      : getListItemHeight({selectionWindowView, isMobile, itemsList});
    prevItemHeight.current = height;
    return height
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionWindowView, isMobile, itemsList.current, measureId, resizing]);

  // No isMounted() guard here on purpose: useIsMounted flips its ref in a passive effect,
  // which runs after layout effects, so it would still report false during the mount-time
  // re-measure below. Both callers are synchronous effects of this component, so the
  // component is alive by definition when they run.
  const remeasure = useCallback(() => setMeasureId(id => id + 1), []);

  // The container ref is only attached after the first render, so `itemHeight` above is
  // always 0 on mount. Re-measure now that the node exists - before paint, so the list
  // never renders with a 0 item height. This matters on every remount of this component,
  // e.g. coming back from the feature info screen (materialsWizardMain renders either
  // <FeatureInfo/> or this list, so closing the info screen mounts it from scratch).
  useLayoutEffect(() => {
    if (itemsList.current) {
      remeasure();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateHeight = () => {
    if (!isMounted()) {
      return
    }
    setResizing(false);
  }

  const onResize = () => {
    if (!resizeId || window.inputIsFocused) {
      return
    }
    // Some interactions on the page (e.g. panning/zooming the docked visualizer,
    // or a scrollbar flickering in/out) can fire a native "resize" event even
    // though the viewport itself hasn't actually changed size. Without this
    // guard every one of those spurious events flips `showItemsList` off for
    // 300ms, which makes every product tile in the list flash its loader again
    // as if it had remounted. Only react to resizes that changed the viewport.
    const {innerWidth, innerHeight} = window;
    if (innerWidth === viewportSize.current.width && innerHeight === viewportSize.current.height) {
      return
    }
    viewportSize.current = {width: innerWidth, height: innerHeight};
    setResizing(true);
    clearTimeout(resizeId.current);
    resizeId.current = window.setTimeout(updateHeight, 300);
  }

  useEffect(() => {
    window.addEventListener("resize", onResize);
    if (browserIsSafari()) {
      window.addEventListener('scroll', onResize);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      if (browserIsSafari()) {
        window.removeEventListener('scroll', onResize);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // console.log("resizing", resizing);
  // console.log("itemHeight", itemHeight);

  useEffect(() => {
    if (!itemHeight) {
      // Re-run the height calculation directly. This used to dispatch a synthetic window
      // "resize" event, but `onResize` now ignores events that didn't actually change the
      // viewport (see the guard above), so that would be a no-op and the list stayed empty.
      remeasure();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bvOpened]);

  const handleFeatureClick = useCallback(async (feature) => {
    if (shouldSwitchVisualizer(feature, "", fabricatorSettings, visualizerIsChanging)) {
      return false
    }
    setWizardSelectedValues({
      [wizardActiveStep]: {
        wizardGroup,
        selectedFeature: feature,
        partId: feature.partId,
      }
    });
    await fixFeature(feature, {
      fromWizardId: wizardActiveItem.id,
      fromWizardStep: wizardActiveStep,
      fromWizardGroupId: wizardGroup.id
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setWizardSelectedValues, wizardActiveStep, wizardGroup]);

  // Runs in SelectionMainList BEFORE the clicked feature is applied (handleFeatureClick above is not awaited there):
  // 1) wizardGroup.clickOnRoomLogic - switch the room (preset) and wait for it
  // 2) if the clicked feature belongs to another RoomLayout - replace it with the same named feature of the current layout
  // Returns the feature synchronously when there is nothing to do.
  const resolveFeatureBeforeClick = useCallback((feature) => {
    if (!shouldResolveWizardFeatureBeforeClick(feature, wizardGroup)
      || shouldSwitchVisualizer(feature, "", fabricatorSettings, visualizerIsChanging)) {
      return feature
    }
    // the flag stays on until the replaced feature is set active (see runWhileRoomSwitching)
    return runWhileRoomSwitching(async () => {
      await switchRoomByWizardGroup(wizardGroup, feature);
      return getWizardFeatureForCurrentRoomLayout(feature, wizardSelectedValues, wizardActiveStep, wizardActiveItem, wizardGroup);
    })
  }, [wizardGroup, wizardSelectedValues, wizardActiveStep, wizardActiveItem, fabricatorSettings, visualizerIsChanging]);

  const checkIfNeedToSwitchRoom = async (featureClicked) => {
    const newVisualizer = shouldSwitchVisualizer(featureClicked, "", fabricatorSettings, visualizerIsChanging);
    if (newVisualizer) {
      await switchToNewVisualizerType(newVisualizer);
    }
  }

  const handleUserLoginClick = (userPopupType = UserPopupTypes.Unlock) => {
    const getQuoteEnabled = fabricatorSettings.enableQuote;
    Analytics.sendClickEvent(getQuoteEnabled ? 'get-quote-feature-selection' : 'user-login');
    if (userIsLoggedIn) {
      if (fabricatorSettings.twoFA) {
        if (!isPhoneNumberValidated) {
          toggleUserPopup({
            userPopupOpened: true,
            userPopupType
          });
        }
      }
    } else {
      toggleUserPopup({
        userPopupOpened: true,
        userPopupType
      });
    }
  };

  const getContent = () => {
    // console.log("wizardMissingFeatures", wizardMissingFeatures);

    let useNewWizardFeaturesRequestsLogic = shouldUseNewWizardFeaturesRequestsLogic(wizardGroup, currentParent);
    const alreadyRequestedWizardFeatures = !!wizardMissingFeatures[getWizardMissingKey(wizardActiveItem, wizardGroup)]; // if it is an array - disable loader
    if (useNewWizardFeaturesRequestsLogic) {
      if (!alreadyRequestedWizardFeatures) {
        return <div className="message-loading-left-panel-spinner message-loading-left-panel-spinner--small"/>
      }
    // } else if (currentParent && !currentParent.allFeaturesReceived) {
    //   return <div className="message-loading-left-panel-spinner message-loading-left-panel-spinner--small"/>
    }
    return <SelectionMainList isWizard={true}
                              wizardGroup={wizardGroup}
                              useNewWizardFeaturesRequestsLogic={useNewWizardFeaturesRequestsLogic}
                              showListForce={useNewWizardFeaturesRequestsLogic && alreadyRequestedWizardFeatures}
                              currentGroups={currentGroups}
                              setRef={setRef}
                              currentParent={currentParent}
                              features={features}
                              listItemHeight={itemHeight}
                              handleFeatureClick={handleFeatureClick}
                              resolveFeatureBeforeClick={resolveFeatureBeforeClick}
      // showItemsList={!this.props.searchRequest && this.state.showItemsList}
                              showItemsList={!resizing}
                              handleUserLoginClick={handleUserLoginClick}
                              layoutName={layoutName}
                              checkIfNeedToSwitchRoom={checkIfNeedToSwitchRoom}
                              parentUpdate={() => updateNavigationCount()}
    />
  }

  return (
    <div className="bv-item-container" ref={itemsList}>
      {getContent()}
    </div>
  )

};

MaterialsWizardMainList.propTypes = {
  currentGroups: PropTypes.arrayOf(PropTypes.shape(wizardGroupInterface)),
  wizardGroup: PropTypes.shape(wizardGroupInterface),
}

const mapStateToProps = (store) => {
  const {wizardList, wizardActiveItem, wizardSelectedValues, wizardActiveStep, wizardMissingFeatures} = store.wizardState;
  const {selectionFeatureInfo, selectionWindowView} = store.selectionState;
  const {filterGroups} = store.filterState;
  const {isMobile, visualizerIsChanging} = store.serverState;
  const {bvOpened} = store.bathroomVisualizerState;
  const {settings} = store.fabricatorState;
  const {userIsLoggedIn, isPhoneNumberValidated} = store.userState;

  return {
    wizardList,
    wizardActiveItem,
    wizardSelectedValues,
    wizardActiveStep,
    selectionFeatureInfo,
    filterGroups,
    selectionWindowView,
    isMobile,
    bvOpened,
    wizardMissingFeatures,
    fabricatorSettings: settings,
    visualizerIsChanging,
    isPhoneNumberValidated,
    userIsLoggedIn,
  };
};

const mapDispatchToProps = {
  setWizardSelectedValues,
  updateNavigationCount,
  toggleUserPopup,
};

export default connect(mapStateToProps, mapDispatchToProps)(MaterialsWizardMainList)
